import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { PublicAuthDialog } from '@/components/hiring/PublicAuthDialog';

interface PublicJobBoardContextValue {
  triggerAuth: (callback?: () => void) => void;
}

const PublicJobBoardContext = createContext<PublicJobBoardContextValue | null>(null);

export function PublicJobBoardProvider({ children }: { children: ReactNode }) {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(null);

  const triggerAuth = useCallback((callback?: () => void) => {
    if (callback) {
      setPendingCallback(() => callback);
    }
    setShowAuthDialog(true);
  }, []);

  const handleAuthSuccess = () => {
    setShowAuthDialog(false);
    
    // Execute pending callback if any
    if (pendingCallback) {
      const cb = pendingCallback;
      setPendingCallback(null);
      cb();
    }
  };

  return (
    <PublicJobBoardContext.Provider value={{ triggerAuth }}>
      {children}
      
      <PublicAuthDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
        onAuthSuccess={handleAuthSuccess}
      />
    </PublicJobBoardContext.Provider>
  );
}

export function usePublicJobBoardAuth() {
  const context = useContext(PublicJobBoardContext);
  if (!context) {
    throw new Error('usePublicJobBoardAuth must be used within PublicJobBoardProvider');
  }
  return context;
}
